import React, { useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import DuplaConfirmacao from "src/v2/components/DuplaConfirmacao/DuplaConfirmacao"
import { COMPANY_HISTORY_CONFIG } from "./company-history-config"
import CompanyRegistrationForm from "../registrations/company-registration-form"
import useCompanyHistory from "./use-company-history"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"
import DetailsModal from "src/v2/components/DetailsModal/details-modal"
import { getCompanyHistoryModalConfig } from "./company-history-modal-config"


const DRAFT_ROW_ID = "new-history-row"

const isDraftRow = (row) => row?.id === DRAFT_ROW_ID

const isEmptyValue = (value) => value === null || value === undefined || (typeof value === "string" && !value.trim())

const getRequiredFieldLabels = (fields, row) => fields
    .filter((field) => field.required && isEmptyValue(row?.[field.name]))
    .map((field) => field.label)

const hasRowChanged = (newRow, oldRow) => Object.keys({ ...newRow, ...oldRow })
    .some((field) => newRow[field] !== oldRow[field])


const CompanyHistoryModal = ({ open, historyType, company, historyData, isAdminUser, onClose, onChanged, onRetry }) => {
    const definition = COMPANY_HISTORY_CONFIG[historyType]
    const historyMutation = useCompanyHistory({
        definition,
        company,
        onChanged,
        loadOnMount: false,
    })
    const [rowToDelete, setRowToDelete] = useState(null)
    const [viewer, setViewer] = useState(null)
    const [draftRow, setDraftRow] = useState(null)
    const canEdit = Boolean(isAdminUser && definition?.editable)
    const usesInlineEditing = definition?.editable?.interaction?.mode === "inline"
    const editableFields = definition?.editable?.fields ?? []
    const historyRows = historyData?.rows ?? []
    const loading = Boolean(historyData?.loading || historyMutation.loading)
    const status = historyData?.loading
        ? "loading"
        : historyData?.error
            ? "error"
            : historyRows.length
                ? "success"
                : "empty"
    const error = historyData?.error ?? null
    
    const editableFieldNames = useMemo(
        () => new Set(editableFields.map((field) => field.name)),
        [editableFields]
    )

    useEffect(() => {
        setDraftRow(null)
    }, [company?.CPFCNPJ, historyType])

    const currentDraftRow = draftRow?.CPFCNPJ === company?.CPFCNPJ ? draftRow : null
    const hasDraftRow = Boolean(currentDraftRow)

    const rows = useMemo(
        () =>
            currentDraftRow
                ? [
                      currentDraftRow,
                      ...historyRows,
                  ]
                : historyRows,
        [currentDraftRow, historyRows]
    )

    const isTableLoading = loading

    const handleClose = useCallback(() => {
        setRowToDelete(null)
        setViewer(null)
        setDraftRow(null)
        onClose()
    }, [onClose])

    const handleModalClose = useCallback(() => {
        if (loading) return

        handleClose()
    }, [handleClose, loading])

    const handleAdd = useCallback(async () => {
        if (!definition?.editable) return

        if (historyRows.length) {
            await historyMutation.mutate({
                method: "post",
                row: definition.editable.createFromLatest({ company, rows: historyRows }),
            })
            return
        }

        setDraftRow({
            ...definition.editable.createFromLatest({ company, rows: [] }),
            id: DRAFT_ROW_ID,
        })
    }, [company, definition, historyMutation.mutate, historyRows])

    const handleInlineRowUpdate = useCallback(async (newRow, oldRow) => {
        if (isDraftRow(newRow)) {
            setDraftRow(newRow)
            return newRow
        }

        if (!hasRowChanged(newRow, oldRow)) return oldRow

        const saved = await historyMutation.mutate({ method: "patch", row: newRow })
        return saved ? newRow : oldRow
    }, [historyMutation.mutate])

    const handleSaveDraft = useCallback(async (row) => {
        const requiredLabels = getRequiredFieldLabels(editableFields, row)
        if (requiredLabels.length) {
            dispatchSnackbar(`Preencha os campos obrigatórios: ${requiredLabels.join(", ")}.`, "error")
            return
        }

        const saved = await historyMutation.mutate({ method: "post", row })
        if (saved) setDraftRow(null)
    }, [editableFields, historyMutation.mutate])

    const handleDiscardDraft =
        useCallback(() => {
            setDraftRow(null)
        }, [])

    const handleRequestDelete =
        useCallback((row) => {
            setRowToDelete(row)
        }, [])

    const handleDelete = useCallback(async () => {
        const deleted = await historyMutation.mutate({ method: "delete", row: rowToDelete })
        if (deleted) setRowToDelete(null)
    },
    [historyMutation.mutate, rowToDelete])    

    const handleView = useCallback((row) => {
        setViewer(row)
    }, [])

    const isCellEditable = useCallback(
        (params) => canEdit && !loading && editableFieldNames.has(params.field),
        [canEdit, editableFieldNames, loading]
    )

    const modalConfig = useMemo(() => {
        if (!definition) return null

        return getCompanyHistoryModalConfig({
            definition,
            company,
            rows,

            loading,
            tableLoading: isTableLoading,
            status,
            error,

            isAdminUser,
            canEdit,
            usesInlineEditing,
            editableFieldNames,
            hasDraftRow,
            isDraftRow,
            isCellEditable,

            handleAdd,
            handleSaveDraft,
            handleDiscardDraft,
            handleDelete:
                handleRequestDelete,
            handleView,
            handleInlineRowUpdate,
            handleRetry: onRetry,
        })
    }, [
        definition,
        company,
        rows,
        loading,
        status,
        error,
        isTableLoading,
        isAdminUser,
        canEdit,
        usesInlineEditing,
        editableFieldNames,
        hasDraftRow,
        isCellEditable,
        handleAdd,
        handleSaveDraft,
        handleDiscardDraft,
        handleRequestDelete,
        handleView,
        handleInlineRowUpdate,
        onRetry,
    ])

    if (!modalConfig) return null


    return (
        <>
            <DetailsModal
                open={open}
                onClose={handleModalClose}
                title={modalConfig.title}
                sections={modalConfig.sections}
                empty={modalConfig.empty}
                emptyText={
                    modalConfig.emptyText
                }
            />

            {viewer && definition.view && (
                <CompanyRegistrationForm
                    open
                    definition={{
                        ...definition,
                        fields:
                            definition.view.fields,
                    }}
                    initialRow={viewer}
                    isNew={false}
                    mode={definition.view.mode}
                    saving={false}
                    onClose={() =>
                        setViewer(null)
                    }
                    onSave={() => false}
                />
            )}

            <DuplaConfirmacao
                open={Boolean(rowToDelete)}
                title="Excluir registro"
                description="Esta ação não pode ser desfeita. Deseja excluir este registro?"
                cancel={() =>
                    setRowToDelete(null)
                }
                confirm={handleDelete}
                confirmTextButton={
                    historyMutation.loading
                        ? "EXCLUINDO..."
                        : "EXCLUIR"
                }
            />
        </>
    )
}

CompanyHistoryModal.propTypes = {
    open: PropTypes.bool.isRequired,
    historyType: PropTypes.oneOf(
        Object.keys(COMPANY_HISTORY_CONFIG)
    ).isRequired,
    company: PropTypes.object,
    historyData: PropTypes.shape({
        rows: PropTypes.array,
        loading: PropTypes.bool,
        error: PropTypes.string,
    }),
    isAdminUser: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onChanged: PropTypes.func,
    onRetry: PropTypes.func,
}

CompanyHistoryModal.defaultProps = {
    company: {},
    historyData: undefined,
    onChanged: undefined,
    onRetry: undefined,
}

export default CompanyHistoryModal